import {
  UseFormRegister,
  FieldErrors,
  UseFormWatch,
} from "react-hook-form";
import { Shield, FileText, HeartPulse, Camera, ExternalLink } from "lucide-react";
import { Input } from "../ui/Input";
import { ConsentFormValues } from "../../lib/consent-schema";
import { Badge } from "../ui/Badge";
import {
  formatConsentDate,
  getConsentStatus,
} from "../../lib/consent-utils";

interface FieldProps {
  register: UseFormRegister<ConsentFormValues>;
  errors: FieldErrors<ConsentFormValues>;
  watch: UseFormWatch<ConsentFormValues>;
  name: string;
  consentValue?: boolean | null;
  consentedAt?: string | null;
}

export function DataProtectionSection({
  register,
  errors,
  name,
  consentValue,
  consentedAt,
}: Pick<FieldProps, "register" | "errors" | "name" | "consentValue" | "consentedAt">) {
  const status = getConsentStatus(consentValue, consentedAt);
  const formattedDate = formatConsentDate(consentedAt);

  return (
    <section className="bg-gray-50 p-4 rounded-lg border border-gray-200">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-primary-600" />
          <h4 className="font-semibold text-gray-900 text-lg">Data Protection</h4>
        </div>
        <a
          href="/policies/data-processing"
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
        >
          View Policy
          <ExternalLink className="w-3 h-3" />
        </a>
      </div>
      <div className="flex items-start space-x-3">
        <input
          {...register("dataProcessingConsent")}
          type="checkbox"
          id="dataProcessingConsent"
          className={`mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500 ${
            errors.dataProcessingConsent ? "border-red-500" : ""
          }`}
        />
        <label htmlFor="dataProcessingConsent" className="text-sm text-gray-700">
          {name} consents to the processing of their personal data for the
          purpose of event management and safety.
        </label>
      </div>
      {formattedDate && (
        <div className="mt-2 flex items-center gap-2">
          <span className="text-xs text-gray-500">
            Last updated: {formattedDate}
          </span>
          {status === "active" ? (
            <Badge variant="success" size="sm">Active</Badge>
          ) : status === "expired" ? (
            <Badge variant="error" size="sm">Expired</Badge>
          ) : status === "declined" ? (
            <Badge variant="warning" size="sm">Declined</Badge>
          ) : null}
        </div>
      )}
      {!formattedDate && (
        <div className="mt-2">
          <span className="text-xs text-gray-500 italic">Not yet provided</span>
        </div>
      )}
      {errors.dataProcessingConsent && (
        <p className="mt-2 text-sm text-red-600">
          {errors.dataProcessingConsent.message}
        </p>
      )}
    </section>
  );
}

export function CodeOfConductSection({
  register,
  errors,
  name,
  consentValue,
  consentedAt,
}: Pick<FieldProps, "register" | "errors" | "name" | "consentValue" | "consentedAt">) {
  const status = getConsentStatus(consentValue, consentedAt);
  const formattedDate = formatConsentDate(consentedAt);

  return (
    <section className="bg-gray-50 p-4 rounded-lg border border-gray-200">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-primary-600" />
          <h4 className="font-semibold text-gray-900 text-lg">Code of Conduct</h4>
        </div>
        <a
          href="/policies/code-of-conduct"
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
        >
          View Policy
          <ExternalLink className="w-3 h-3" />
        </a>
      </div>
      <div className="flex items-start space-x-3">
        <input
          {...register("consentFormCodeOfConductAccepted")}
          type="checkbox"
          id="consentFormCodeOfConductAccepted"
          className={`mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500 ${
            errors.consentFormCodeOfConductAccepted ? "border-red-500" : ""
          }`}
        />
        <label
          htmlFor="consentFormCodeOfConductAccepted"
          className="text-sm text-gray-700"
        >
          {name} agrees to abide by the club's code of conduct and behave
          respectfully towards all participants and staff.
        </label>
      </div>
      {formattedDate && (
        <div className="mt-2 flex items-center gap-2">
          <span className="text-xs text-gray-500">
            Last updated: {formattedDate}
          </span>
          {status === "active" ? (
            <Badge variant="success" size="sm">Active</Badge>
          ) : status === "expired" ? (
            <Badge variant="error" size="sm">Expired</Badge>
          ) : status === "declined" ? (
            <Badge variant="warning" size="sm">Declined</Badge>
          ) : null}
        </div>
      )}
      {!formattedDate && (
        <div className="mt-2">
          <span className="text-xs text-gray-500 italic">Not yet provided</span>
        </div>
      )}
      {errors.consentFormCodeOfConductAccepted && (
        <p className="mt-2 text-sm text-red-600">
          {errors.consentFormCodeOfConductAccepted.message}
        </p>
      )}
    </section>
  );
}

export function PersonalDetailsSection({
  register,
  errors,
  watch,
  name,
}: FieldProps) {
  const isUnder18 = watch("isUnder18") === true;

  return (
    <section className="bg-gray-50 p-4 rounded-lg border border-gray-200">
      <div className="flex items-center gap-2 mb-4">
        <FileText className="w-5 h-5 text-primary-600" />
        <h4 className="font-semibold text-gray-900 text-lg">
          Personal Information
        </h4>
      </div>

      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <div className="flex items-center space-x-2 bg-white p-3 rounded border border-gray-200">
              <input
                {...register("isUnder18")}
                type="checkbox"
                id="isUnder18"
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <label
                htmlFor="isUnder18"
                className="text-sm font-medium text-gray-700"
              >
                {name} is under 18 years old
              </label>
            </div>
          </div>

          {isUnder18 && (
            <Input
              label="Date of Birth"
              type="date"
              {...register("consentFormParticipantDateOfBirth")}
              error={errors.consentFormParticipantDateOfBirth?.message}
            />
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Pronouns
            </label>
            <select
              {...register("consentFormParticipantPronouns")}
              className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:border-transparent sm:text-sm ${
                errors.consentFormParticipantPronouns
                  ? "border-red-300 focus:ring-red-500"
                  : "border-gray-300 focus:ring-primary-500"
              }`}
            >
              <option value="">Select pronouns</option>
              <option value="He/Him">He/Him</option>
              <option value="She/Her">She/Her</option>
              <option value="They/Them">They/Them</option>
              <option value="Prefer not to say">Prefer not to say</option>
            </select>
            {errors.consentFormParticipantPronouns && (
              <p className="mt-1 text-sm text-red-600">
                {errors.consentFormParticipantPronouns.message}
              </p>
            )}
          </div>
          <Input
            label="Address"
            multiline
            rows={2}
            className="md:col-span-2"
            {...register("consentFormParticipantAddress")}
            error={errors.consentFormParticipantAddress?.message}
          />
        </div>

        {isUnder18 && (
          <div className="pt-4 border-t border-gray-200">
            <h5 className="font-medium text-gray-900 mb-4">
              Parent/Carer Information
            </h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Parent First Name"
                {...register("consentFormParentCarerFirstName")}
              />
              <Input
                label="Parent Surname"
                {...register("consentFormParentCarerSurname")}
              />
              <Input
                label="Parent Address"
                multiline
                rows={2}
                className="md:col-span-2"
                {...register("consentFormParentCarerAddress")}
              />
              <Input
                label="Relationship"
                {...register("consentFormParentCarerRelationship")}
              />
              <Input
                label="Contact Number"
                {...register("consentFormParentCarerContactNumber")}
              />
            </div>
          </div>
        )}

        <div className="pt-4 border-t border-gray-200">
          <h5 className="font-medium text-gray-900 mb-4">Emergency Contacts</h5>
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label="Contact 1 Name"
                {...register("consentFormEmergencyContact1Name")}
                error={errors.consentFormEmergencyContact1Name?.message}
              />
              <Input
                label="Relationship"
                {...register("consentFormEmergencyContact1Relationship")}
                error={errors.consentFormEmergencyContact1Relationship?.message}
              />
              <Input
                label="Phone"
                {...register("consentFormEmergencyContact1Phone")}
                error={errors.consentFormEmergencyContact1Phone?.message}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label="Contact 2 Name"
                {...register("consentFormEmergencyContact2Name")}
                error={errors.consentFormEmergencyContact2Name?.message}
              />
              <Input
                label="Relationship"
                {...register("consentFormEmergencyContact2Relationship")}
                error={errors.consentFormEmergencyContact2Relationship?.message}
              />
              <Input
                label="Phone"
                {...register("consentFormEmergencyContact2Phone")}
                error={errors.consentFormEmergencyContact2Phone?.message}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function MedicalSection({
  register,
  errors,
  watch,
  name,
  consentValue,
  consentedAt,
}: FieldProps) {
  const hasMedication = watch("hasMedication") === true;
  const hasInjuries = watch("hasInjuries") === true;
  const hasDietaryRequirements = watch("hasDietaryRequirements") === true;
  const hasDisability = watch("hasDisability") === true;

  return (
    <section className="bg-gray-50 p-4 rounded-lg border border-gray-200">
      <div className="flex items-center gap-2 mb-4">
        <HeartPulse className="w-5 h-5 text-primary-600" />
        <h4 className="font-semibold text-gray-900 text-lg">
          Medical & Emergency Treatment
        </h4>
      </div>

      <div className="space-y-6">
        <div className="space-y-6">
          <Input
            label="Important Medical Information (Optional)"
            multiline
            rows={3}
            placeholder="Any important medical information we should know about"
            {...register("consentFormImportantMedicalInformation")}
            error={errors.consentFormImportantMedicalInformation?.message}
          />

          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <input
                {...register("hasMedication")}
                type="checkbox"
                id="hasMedication"
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <label htmlFor="hasMedication" className="text-sm font-medium">
                {name} takes medication
              </label>
            </div>
            {hasMedication && (
              <Input
                label="Medication details"
                multiline
                rows={2}
                placeholder="Please describe the medication"
                {...register("consentFormMedicationDetails")}
                error={errors.consentFormMedicationDetails?.message}
              />
            )}
          </div>

          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <input
                {...register("hasInjuries")}
                type="checkbox"
                id="hasInjuries"
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <label htmlFor="hasInjuries" className="text-sm font-medium">
                {name} has injuries
              </label>
            </div>
            {hasInjuries && (
              <Input
                label="Injury details"
                multiline
                rows={2}
                placeholder="Please describe the injuries"
                {...register("consentFormInjuriesDetails")}
                error={errors.consentFormInjuriesDetails?.message}
              />
            )}
          </div>

          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <input
                {...register("hasDietaryRequirements")}
                type="checkbox"
                id="hasDietaryRequirements"
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <label
                htmlFor="hasDietaryRequirements"
                className="text-sm font-medium"
              >
                {name} has dietary requirements
              </label>
            </div>
            {hasDietaryRequirements && (
              <Input
                label="Dietary requirement details"
                multiline
                rows={2}
                placeholder="Please describe the dietary requirements"
                {...register("consentFormDietaryRequirementsDetails")}
                error={errors.consentFormDietaryRequirementsDetails?.message}
              />
            )}
          </div>

          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <input
                {...register("hasDisability")}
                type="checkbox"
                id="hasDisability"
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <label htmlFor="hasDisability" className="text-sm font-medium">
                {name} has a disability
              </label>
            </div>
            {hasDisability && (
              <Input
                label="Disability details"
                multiline
                rows={2}
                placeholder="Please describe how we can support you"
                {...register("consentFormDisabilityDetails")}
                error={errors.consentFormDisabilityDetails?.message}
              />
            )}
          </div>
        </div>

        <div className="pt-4 border-t border-gray-200">
          <div className="flex items-start space-x-3">
            <input
              {...register("emergencyTreatmentConsent")}
              type="checkbox"
              id="emergencyTreatmentConsent"
              className={`mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500 ${
                errors.emergencyTreatmentConsent ? "border-red-500" : ""
              }`}
            />
            <label
              htmlFor="emergencyTreatmentConsent"
              className="text-sm text-gray-700 font-medium"
            >
              {name} consents to emergency medical treatment if required during
              events.
            </label>
          </div>
          {(() => {
            const status = getConsentStatus(consentValue, consentedAt);
            const formattedDate = formatConsentDate(consentedAt);
            return (
              <>
                {formattedDate && (
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-xs text-gray-500">
                      Last updated: {formattedDate}
                    </span>
                    {status === "active" ? (
                      <Badge variant="success" size="sm">Active</Badge>
                    ) : status === "expired" ? (
                      <Badge variant="error" size="sm">Expired</Badge>
                    ) : status === "declined" ? (
                      <Badge variant="warning" size="sm">Declined</Badge>
                    ) : null}
                  </div>
                )}
                {!formattedDate && (
                  <div className="mt-2">
                    <span className="text-xs text-gray-500 italic">Not yet provided</span>
                  </div>
                )}
              </>
            );
          })()}
          {errors.emergencyTreatmentConsent && (
            <p className="mt-2 text-sm text-red-600">
              {errors.emergencyTreatmentConsent.message}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

export function MarketingConsentSection({
  register,
  name,
  consentValue,
  consentedAt,
}: Pick<FieldProps, "register" | "name" | "consentValue" | "consentedAt">) {
  const status = getConsentStatus(consentValue, consentedAt);
  const formattedDate = formatConsentDate(consentedAt);

  return (
    <section className="bg-gray-50 p-4 rounded-lg border border-gray-200">
      <div className="flex items-center gap-2 mb-4">
        <Shield className="w-5 h-5 text-primary-600" />
        <h4 className="font-semibold text-gray-900 text-lg">
          Marketing Communications
        </h4>
      </div>
      <div className="flex items-start space-x-3">
        <input
          {...register("marketingConsent")}
          type="checkbox"
          id="marketingConsent"
          className="mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <label htmlFor="marketingConsent" className="text-sm text-gray-700">
          {name} would like to receive information about future events and club
          activities.
        </label>
      </div>
      {formattedDate && (
        <div className="mt-2 flex items-center gap-2">
          <span className="text-xs text-gray-500">
            Last updated: {formattedDate}
          </span>
          {status === "active" ? (
            <Badge variant="success" size="sm">Active</Badge>
          ) : status === "expired" ? (
            <Badge variant="error" size="sm">Expired</Badge>
          ) : status === "declined" ? (
            <Badge variant="warning" size="sm">Declined</Badge>
          ) : null}
        </div>
      )}
      {!formattedDate && (
        <div className="mt-2">
          <span className="text-xs text-gray-500 italic">Not yet provided</span>
        </div>
      )}
    </section>
  );
}

export function PhotoConsentSection({
  register,
  errors,
  name,
  consentValue,
  consentedAt,
}: Pick<FieldProps, "register" | "errors" | "name" | "consentValue" | "consentedAt">) {
  const status = getConsentStatus(consentValue, consentedAt, true);
  const formattedDate = formatConsentDate(consentedAt);

  return (
    <section className="bg-gray-50 p-4 rounded-lg border border-gray-200">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Camera className="w-5 h-5 text-primary-600" />
          <h4 className="font-semibold text-gray-900 text-lg">Photo Consent</h4>
        </div>
        <a
          href="/policies/photo-consent"
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1"
        >
          View Policy
          <ExternalLink className="w-3 h-3" />
        </a>
      </div>
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Does {name} consent to being photographed during the event for
          promotional purposes?
        </p>
        <div className="space-y-3">
          <div className="flex items-center space-x-3">
            <input
              {...register("photoConsent")}
              type="radio"
              value="true"
              id="photoConsentYes"
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <label htmlFor="photoConsentYes" className="text-sm text-gray-700">
              Yes, {name} consents to being photographed
            </label>
          </div>
          <div className="flex items-center space-x-3">
            <input
              {...register("photoConsent")}
              type="radio"
              value="false"
              id="photoConsentNo"
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <label htmlFor="photoConsentNo" className="text-sm text-gray-700">
              No, {name} does not consent to being photographed
            </label>
          </div>
        </div>
        {formattedDate && (
          <div className="mt-2 flex items-center gap-2">
            <span className="text-xs text-gray-500">
              Last updated: {formattedDate}
            </span>
            {status === "active" ? (
              <Badge variant="success" size="sm">Active</Badge>
            ) : status === "expired" ? (
              <Badge variant="error" size="sm">Expired</Badge>
            ) : null}
          </div>
        )}
        {!formattedDate && (
          <div className="mt-2">
            <span className="text-xs text-gray-500 italic">Not yet provided</span>
          </div>
        )}
        {errors.photoConsent && (
          <p className="mt-2 text-sm text-red-600">
            {errors.photoConsent.message}
          </p>
        )}
      </div>
    </section>
  );
}
